import React, { useState, useEffect } from 'react';
import Nav from './Nav';
import Footer from './Footer';
import { Link } from 'react-router-dom';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import './salesquote.css';
import { Box, Typography, IconButton } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const pad = (n) => String(n).padStart(2, '0');

const toDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

// Local calendar day as YYYY-MM-DD, comparable with <input type="date"> values.
const dayKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return `${date.getDate()} ${MONTHS[date.getMonth()].slice(0, 3)} ${date.getFullYear()}, ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const dateSearchText = (value) => {
  const date = toDate(value);
  if (!date) return '';
  const d = date.getDate();
  const m = date.getMonth();
  const y = date.getFullYear();
  return [
    dayKey(date),
    `${pad(d)}/${pad(m + 1)}/${y}`,
    `${d}/${m + 1}/${y}`,
    `${d} ${MONTHS[m]} ${y}`,
    `${d} ${MONTHS[m].slice(0, 3)} ${y}`,
  ].join(' ');
};

const dateInputStyle = { padding: '6px', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #ccc' };

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDateValue, setToDateValue] = useState('');

  // Fetch FAQs from the database
  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_EXPRESS_SERVER}/faq`);
        const isJson = String(response.headers.get('content-type')).includes('application/json');
        const data = isJson ? await response.json() : null;
        if (response.ok && Array.isArray(data)) {
          setFaqs(data);
        } else {
          console.error('Failed to fetch FAQs:', data?.error || `server returned ${response.status}`);
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      }
    };

    fetchFAQs();
  }, []);

  const term = searchTerm.trim().toLowerCase();
  const filteredFaqs = faqs.filter((faq) => {
    const asked = toDate(faq.createdAt);
    if (fromDate || toDateValue) {
      if (!asked) return false;
      const day = dayKey(asked);
      if (fromDate && day < fromDate) return false;
      if (toDateValue && day > toDateValue) return false;
    }
    if (!term) return true;
    return [faq.title, faq.email, faq.qtn, dateSearchText(faq.createdAt)].some((value) =>
      String(value ?? '').toLowerCase().includes(term)
    );
  });

  const filtersActive = Boolean(term || fromDate || toDateValue);
  const clearFilters = () => {
    setSearchTerm('');
    setFromDate('');
    setToDateValue('');
  };

  return (
    <div className="Home">
      <Nav />
      <div className="dash-main">
        <div className="squote">
          {/* Header Section */}
          <Box
            display="flex"
            alignItems="center"
            sx={{ padding: "8px 16px", backgroundColor: "#f5f5f5", height: "48px" }}
          >
            <IconButton component={Link} to="/">
              <ArrowBackIcon sx={{ fontSize: "1.5rem" }} />
            </IconButton>
            <Typography variant="h6" sx={{ marginLeft: "8px", fontSize: "1rem" }}>
              Frequently Asked Questions
            </Typography>
          </Box>

          {/* Search Bar */}
          <Box
            sx={{ paddingTop: "8px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px" }}
          >
            <input
              type="text"
              placeholder="Search subject, email, inquiry or date (e.g. 27 Feb 2026)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='table-search'
              style={{ minWidth: '320px', width: '30%', padding: '8px', fontSize: '1rem' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              From
              <input
                type="date"
                value={fromDate}
                max={toDateValue || undefined}
                onChange={(e) => setFromDate(e.target.value)}
                style={dateInputStyle}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              To
              <input
                type="date"
                value={toDateValue}
                min={fromDate || undefined}
                onChange={(e) => setToDateValue(e.target.value)}
                style={dateInputStyle}
              />
            </label>
            {filtersActive && (
              <button type="button" onClick={clearFilters} style={{ ...dateInputStyle, cursor: 'pointer', background: 'white' }}>
                Clear filters
              </button>
            )}
            <Typography sx={{ color: '#666', fontSize: '0.85rem' }}>
              {filtersActive
                ? `${filteredFaqs.length} of ${faqs.length} questions`
                : `${faqs.length} questions`}
            </Typography>
          </Box>

          {/* Accordion Section */}
          <div style={{ paddingTop: "8px" }}>
            {filteredFaqs.length === 0 && (
              <Typography sx={{ padding: '16px', color: '#666' }}>
                {faqs.length ? 'No questions match your filters.' : 'No questions have been asked yet.'}
              </Typography>
            )}
            {filteredFaqs.map((faq, index) => (
              <Accordion key={faq.id ?? index} defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`faq-content-${index}`}
                  id={`faq-header-${index}`}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 2, pr: 1 }}>
                    <Typography component="span">{faq.title}</Typography>
                    <Typography component="span" sx={{ color: '#666', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {formatDate(faq.createdAt)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    <strong>Email:</strong> {faq.email}
                    <br />
                    <strong>Asked on:</strong> {formatDate(faq.createdAt) || '—'}
                    {faq.updatedAt && faq.updatedAt !== faq.createdAt && (
                      <>
                        <br />
                        <strong>Last updated:</strong> {formatDate(faq.updatedAt)}
                      </>
                    )}
                    <br />
                    <strong>Inquiry:</strong> {faq.qtn}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default FAQPage;